import './NachPhase.css';
import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import vorData from './vorData.json';
import PerspChart, { buildSeries } from './PerspChart.jsx';

const VELOCITY_THRESHOLD = 0.15;
const DISTANCE_THRESHOLD = 20;

// ── 4-point vote label maps ──
const VOTE_LABEL = {
  en: {
    strongly_disagree: 'Strongly disagree',
    disagree:          'Disagree',
    agree:             'Agree',
    strongly_agree:    'Strongly agree',
  },
  de: {
    strongly_disagree: 'Stark dagegen',
    disagree:          'Dagegen',
    agree:             'Dafür',
    strongly_agree:    'Stark dafür',
  },
  fr: {
    strongly_disagree: 'Fortement contre',
    disagree:          'Contre',
    agree:             'Pour',
    strongly_agree:    'Fortement pour',
  },
};

const AGREE_SIDE       = ['agree', 'strongly_agree'];
const AGREE_STRENGTH   = { strongly_disagree: 0, disagree: 1, agree: 2, strongly_agree: 3 };

// ── Mock: user's personal shift across the conference ──
// Uses one representative thesis per topic. 3 of 4 positions shifted.
const PERSONAL_SHIFT = [
  { topicId: 'transatlantic', before: 'disagree',       after: 'agree'          }, // shifted
  { topicId: 'ai',            before: 'strongly_agree', after: 'strongly_agree' }, // unchanged
  { topicId: 'eu_defence',    before: 'agree',           after: 'strongly_agree' }, // strengthened
  { topicId: 'ukraine',       before: 'disagree',        after: 'agree'          }, // shifted
];


export default function NachPhase() {
  const history      = useHistory();
  const { t, i18n } = useTranslation();
  const lang         = i18n.language?.slice(0, 2) || 'en';

  const isTablet = typeof window !== 'undefined' && window.innerWidth >= 768;

  const getTitle = (obj) => {
    if (lang === 'de') return obj.title_de;
    if (lang === 'fr') return obj.title_fr;
    return obj.title_en;
  };

  const voteLabel = (key) => {
    const map = VOTE_LABEL[lang] ?? VOTE_LABEL.en;
    return map[key] ?? key;
  };

  // ── Thesis t2 used for Community Mood chart (hero diverging story) ──
  const communityThesis = vorData.theses.find((th) => th.id === 't2');
  const communitySeries = communityThesis ? buildSeries(communityThesis, lang) : [];
  const communityText   = communityThesis
    ? (lang === 'de' ? communityThesis.text_de : lang === 'fr' ? communityThesis.text_fr : communityThesis.text_en)
    : '';

  const [currentIdx, setCurrentIdx] = useState(0);

  const trackRef = useRef(null);
  const idxRef   = useRef(0);

  // ── Low-level move ──
  const moveTo = (idx, animated = true, snapBack = false) => {
    const track = trackRef.current;
    if (!track) return;
    if (animated) {
      track.getBoundingClientRect(); // flush layout so dragged position is committed before transition
      track.style.transition = snapBack
        ? 'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)'
        : 'transform 0.9s cubic-bezier(0.16, 1, 0.3, 1)';
    } else {
      track.style.transition = 'none';
    }
    track.style.transform = `translateY(calc(-${idx} * 100dvh))`;
  };

  // ── Navigate - clamped to 2 slides (index 0 and 1) ──
  const navigateTo = (idx) => {
    const clamped = Math.max(0, Math.min(1, idx));
    const track = trackRef.current;
    if (track) {
      const live = getComputedStyle(track).transform;
      track.style.transition = 'none';
      track.style.transform = live;
    }
    setCurrentIdx(clamped);
    idxRef.current = clamped;
    moveTo(clamped, true);
  };

  // ── Gesture + measure setup ──
  useEffect(() => {
    moveTo(0, false);

    const measure = () => {
      const navbar = document.querySelector('.navbar');
      const dock   = document.querySelector('.nach-dock');
      if (navbar) document.documentElement.style.setProperty('--nach-nav-h',  navbar.getBoundingClientRect().height + 'px');
      if (dock)   document.documentElement.style.setProperty('--nach-dock-h', dock.getBoundingClientRect().height   + 'px');
    };
    const timer = setTimeout(measure, 60);
    window.addEventListener('resize', measure);

    const touch = { startY: 0, lastY: 0, lastT: 0, velocity: 0 };

    const onTouchStart = (e) => {
      touch.startY   = e.touches[0].clientY;
      touch.lastY    = touch.startY;
      touch.lastT    = Date.now();
      touch.velocity = 0;
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      const now = Date.now();
      const y   = e.touches[0].clientY;
      const dt  = now - touch.lastT;
      if (dt > 0) touch.velocity = (touch.lastY - y) / dt;
      touch.lastY = y;
      touch.lastT = now;
      const dragOffset = touch.startY - y;
      const track = trackRef.current;
      if (track) {
        track.style.transition = 'none';
        track.style.transform = `translateY(calc(-${idxRef.current} * 100dvh - ${dragOffset}px))`;
      }
    };

    const onTouchEnd = () => {
      const distance = touch.startY - touch.lastY;
      const triggered =
        Math.abs(touch.velocity) > VELOCITY_THRESHOLD ||
        Math.abs(distance)       > DISTANCE_THRESHOLD;
      if (triggered) {
        navigateTo(idxRef.current + (distance > 0 ? 1 : -1));
      } else {
        moveTo(idxRef.current, true, true); // snapBack
      }
    };

    let wheelTimer = null;
    let wheelDelta = 0;
    const onWheel = (e) => {
      e.preventDefault();
      wheelDelta += e.deltaY;
      const track = trackRef.current;
      if (track) {
        track.style.transition = 'none';
        track.style.transform = `translateY(calc(-${idxRef.current} * 100dvh - ${wheelDelta}px))`;
      }
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        const triggered = Math.abs(wheelDelta) > DISTANCE_THRESHOLD;
        if (triggered) {
          navigateTo(idxRef.current + (wheelDelta > 0 ? 1 : -1));
        } else {
          moveTo(idxRef.current, true, true); // snapBack
        }
        wheelDelta = 0;
      }, 80);
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true  });
    document.addEventListener('touchmove',  onTouchMove,  { passive: false });
    document.addEventListener('touchend',   onTouchEnd,   { passive: true  });
    document.addEventListener('wheel',      onWheel,      { passive: false });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measure);
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove',  onTouchMove);
      document.removeEventListener('touchend',   onTouchEnd);
      document.removeEventListener('wheel',      onWheel);
      clearTimeout(wheelTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="nach-root">

      {/* ── Slide track - 2 slides ── */}
      <div className="nach-slides-track" ref={trackRef}>

        {/* ═══════════════════════════════════════════
            SLIDE 1 - YOUR SHIFT
            Personal before→after across the big questions.
            Uses mocked data gracefully representing the demo flow.
            ═══════════════════════════════════════════ */}
        <div className="nach-slide">
          <div className="vor-slide-inner">

            <div className="vor-slide-header">
              <span className="vor-phase-badge">{t('nach.phase')}</span>
              <span className="vor-slide-progress">1 / 2</span>
            </div>

            <div className="vor-slide-topic">{t('nach.slide1.topic')}</div>
            <p className="nach-slide-desc">{t('nach.slide1.desc')}</p>

            {/* Before → after comparison - full text labels */}
            <div className="nach-comparison">
              {PERSONAL_SHIFT.map((row, i) => {
                const topic      = vorData.topics.find(tp => tp.id === row.topicId);
                const changed    = row.before !== row.after;
                const afterAgree = AGREE_SIDE.includes(row.after);
                const stronger   = AGREE_STRENGTH[row.after] > AGREE_STRENGTH[row.before];
                return (
                  <div key={i} className="nach-comparison-row">
                    <div className="nach-row-topic-label">{topic ? getTitle(topic) : row.topicId}</div>
                    <div className="nach-row-labels">
                      <span className="nach-row-before-label">{voteLabel(row.before)}</span>
                      <span className="nach-row-sep">→</span>
                      <span className={`nach-row-after-label${afterAgree ? ' agree' : ' disagree'}${changed ? ' changed' : ''}`}>
                        {voteLabel(row.after)}
                      </span>
                      {changed && (
                        <span className={`nach-row-trend${stronger ? ' up' : ' down'}`}>
                          {stronger ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="nach-shift-summary">
              <span className="nach-shift-dot" />
              {t('nach.slide1.summary')}
            </p>

          </div>
        </div>

        {/* ═══════════════════════════════════════════
            SLIDE 2 - COMMUNITY PERSPECTIVE
            How the conference moved opinion across perspectives.
            This is the payoff: the elite-public gap and its shift.
            ═══════════════════════════════════════════ */}
        <div className="nach-slide">
          <div className="vor-slide-inner">

            <div className="vor-slide-header">
              <span className="vor-phase-badge">{t('nach.phase')}</span>
              <span className="vor-slide-progress">2 / 2</span>
            </div>

            <div className="vor-slide-topic">{t('nach.slide2.topic')}</div>
            <p className="nach-section-label">{t('nach.slide2.sectionLabel')}</p>
            <p className="nach-community-statement">
              <span className="nach-q">&ldquo;</span>
              {communityText}
              <span className="nach-q">&rdquo;</span>
            </p>

            {/* Opinion-shift chart - reuses PerspChart from ThesisDetail */}
            {communityThesis && (
              <PerspChart
                series={communitySeries}
                categories={[t('td.phase.before'), t('td.phase.during'), t('td.phase.after')]}
                scaleLabels={[
                  t('bq.strongly_agree'),
                  t('bq.agree'),
                  t('bq.disagree'),
                  t('bq.strongly_disagree'),
                ]}
                phase="after"
                isTablet={isTablet}
              />
            )}

            <p className="nach-footnote">{t('nach.slide2.footnote')}</p>

          </div>
        </div>

      </div>

      {/* ── Progress dots - 2 slides ── */}
      <div className="takes-dots">
        {[0, 1].map(i => (
          <div
            key={i}
            className={`takes-dot${i === currentIdx ? ' active' : ''}`}
            onClick={() => navigateTo(i)}
          />
        ))}
      </div>

      {/* ── Bottom dock ── */}
      <div className="nach-dock">
        <div className="nach-dock-inner">
          {currentIdx < 1 ? (
            <button className="vor-cta-btn active nach-dock-btn" onClick={() => navigateTo(currentIdx + 1)}>
              {t('nach.next')}
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 3l5 5-5 5" />
              </svg>
            </button>
          ) : (
            <button className="vor-cta-btn active nach-dock-btn" onClick={() => history.push('/takes')}>
              {t('nach.finish')}
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 3l5 5-5 5" />
              </svg>
            </button>
          )}
        </div>
        <div className="takes-proto">Prototype by Ferdinand Riedl · riedlsoftware.com</div>
      </div>

    </div>
  );
}
